/**
 * Image URL utilities
 * Builds full URLs from relative paths stored in the database
 */

const S3_PUBLIC_URL = import.meta.env.VITE_S3_URL || '';
const S3_PATH_PREFIX = import.meta.env.VITE_S3_PATH_PREFIX || '';

/**
 * Build a full image URL from a relative path
 * If the path is already a full URL (starts with http), returns it as-is
 * If no S3_PUBLIC_URL is configured, returns the path as-is
 *
 * Supports both:
 * - New minimal paths: posts/uuid.jpg
 * - Legacy paths: /feedbackhub-uploads/posts/uuid.jpg
 */
export function getImageUrl(path: string | null | undefined): string | null {
  if (!path) return null;

  // Already a full URL
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // No S3 URL configured, return path as-is (won't work but avoids errors)
  if (!S3_PUBLIC_URL) {
    return path;
  }

  // Normalize - remove leading slash
  let cleanPath = path.startsWith('/') ? path.slice(1) : path;

  // Strip legacy bucket prefix (backward compatibility)
  const legacyPrefix = 'feedbackhub-uploads/';
  if (cleanPath.startsWith(legacyPrefix)) {
    cleanPath = cleanPath.slice(legacyPrefix.length);
  }

  // Build URL: base + prefix + / + path
  const prefix = S3_PATH_PREFIX || '';
  return `${S3_PUBLIC_URL}${prefix}/${cleanPath}`;
}

/**
 * Build full URLs for an array of image paths
 */
export function getImageUrls(paths: string[] | null | undefined): string[] {
  if (!paths || paths.length === 0) return [];
  return paths.map(path => getImageUrl(path)).filter((url): url is string => url !== null);
}
